// src/components/admin/AboutUsConfigEditor.jsx
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { database } from "../../firebaseConfig";
import { ref, get, set } from "firebase/database";
import "./AboutUsConfigEditor.css";
import { config } from '../../config/config'
const API_URL = config.baseUrl;

const AboutUsConfigEditor = () => {
    // Start with an empty object
    const [config, setConfig] = useState({});
    const [loading, setLoading] = useState(true);

    // Refs for file inputs.
    const heroDesktopInputRef = useRef(null);
    const heroMobileInputRef = useRef(null);
    const attractionInputRef = useRef(null);

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const snapshot = await get(ref(database, "aboutusconfig"));
                let fetchedConfig = snapshot.exists() ? snapshot.val() : {};

                // Ensure each section exists.
                if (!fetchedConfig.general) {
                    fetchedConfig.general = { bodyBackgroundColor: "#ffffff" };
                }
                if (!fetchedConfig.hero) {
                    fetchedConfig.hero = {
                        desktopImageUrl: "",
                        mobileImageUrl: "",
                        backgroundColor: "#ffffff",
                        order: 1,
                        text: "",
                    };
                }
                if (!fetchedConfig.attraction) {
                    fetchedConfig.attraction = { order: 4, images: [] };
                } else if (!Array.isArray(fetchedConfig.attraction.images)) {
                    fetchedConfig.attraction.images = [];
                }
                if (!fetchedConfig.certificates) {
                    fetchedConfig.certificates = [];
                }
                if (!fetchedConfig.seo) {
                    fetchedConfig.seo = {};
                }

                setConfig(fetchedConfig);
            } catch (error) {
                console.error("Error fetching About Us config:", error);
                // Fallback to an empty structure that the UI can handle.
                setConfig({
                    general: { bodyBackgroundColor: "#ffffff" },
                    hero: {
                        desktopImageUrl: "",
                        mobileImageUrl: "",
                        backgroundColor: "#ffffff",
                        order: 1,
                        text: "",
                    },
                    attraction: { order: 4, images: [] },
                    certificates: [],
                    seo: {},
                });
            }
            setLoading(false);
        };

        fetchConfig();
    }, []);

    // Helper: Upload file to server.
    const uploadFile = async (file) => {
        if (!file) return "";
        const formData = new FormData();
        formData.append("media", file);
        try {
            const response = await axios.post(`${API_URL}upload`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return response.data.fileUrl || "";
        } catch (error) {
            console.error("Error uploading file:", error);
            return "";
        }
    };

    // Helper: Delete file.
    const deleteFile = async (fileUrl) => {
        if (!fileUrl) return;
        const parts = fileUrl.split("/");
        const filename = parts[parts.length - 1];
        try {
            await axios.delete(`${API_URL}delete`, { data: { filename } });
            console.log("Deleted file:", filename);
        } catch (error) {
            console.error("Error deleting file:", error);
        }
    };

    // Hero image change handlers.
    const handleHeroDesktopChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            if (config.hero?.desktopImageUrl) {
                await deleteFile(config.hero.desktopImageUrl);
            }
            const newUrl = await uploadFile(file);
            setConfig((prev) => ({
                ...prev,
                hero: { ...prev.hero, desktopImageUrl: newUrl },
            }));
        }
    };

    const handleHeroMobileChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            if (config.hero?.mobileImageUrl) {
                await deleteFile(config.hero.mobileImageUrl);
            }
            const newUrl = await uploadFile(file);
            setConfig((prev) => ({
                ...prev,
                hero: { ...prev.hero, mobileImageUrl: newUrl },
            }));
        }
    };

    // Handle simple changes.
    const handleSimpleChange = (section, field, value) => {
        setConfig((prev) => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value,
            },
        }));
    };

    // Attraction section: handle multiple file selection.
    const handleAttractionMultipleChange = async (e) => {
        const files = e.target.files;
        if (files.length) {
            const uploadedImages = await Promise.all(
                Array.from(files).map(async (file) => {
                    const fileUrl = await uploadFile(file);
                    return { id: Date.now() + Math.random(), imageUrl: fileUrl };
                })
            );
            setConfig((prev) => ({
                ...prev,
                attraction: {
                    ...prev.attraction,
                    images: [
                        ...(prev.attraction?.images || []),
                        ...uploadedImages,
                    ],
                },
            }));
            attractionInputRef.current.value = "";
        }
    };

    // Remove an image from the attraction images array.
    const removeAttractionImage = async (index) => {
        const currentItem = config.attraction.images[index];
        if (currentItem.imageUrl) {
            await deleteFile(currentItem.imageUrl);
            const updatedImages = config.attraction.images.filter((_, i) => i !== index);
            setConfig((prev) => ({
                ...prev,
                attraction: {
                    ...prev.attraction,
                    images: updatedImages,
                },
            }));
        }
    };

    // Certificates section: handle image change for certificate items.
    const handleCertificateImageChange = async (index, e) => {
        const file = e.target.files[0];
        if (file) {
            const currentItem = config.certificates[index];
            if (currentItem.imageUrl) {
                await deleteFile(currentItem.imageUrl);
            }
            const newUrl = await uploadFile(file);
            const updatedCertificates = [...config.certificates];
            updatedCertificates[index] = {
                ...updatedCertificates[index],
                imageUrl: newUrl,
            };
            setConfig((prev) => ({
                ...prev,
                certificates: updatedCertificates,
            }));
        }
    };

    // Add new certificate item.
    const addArrayItem = (section) => {
        if (section === "certificates") {
            const newItem = {
                id: Date.now(),
                imageUrl: "",
                description: "",
                flexDirection: "row",
                order: 1,
                backgroundColor: "#ffffff",
            };
            setConfig((prev) => ({
                ...prev,
                certificates: [...(prev.certificates || []), newItem],
            }));
        }
    };

    // Delete a certificate item.
    const deleteArrayItem = (section, index) => {
        if (section === "certificates") {
            const updatedArray = config.certificates.filter((_, i) => i !== index);
            setConfig((prev) => ({
                ...prev,
                certificates: updatedArray,
            }));
        }
    };

    // Save configuration to Firebase.
    const handleSaveConfig = async () => {
        try {
            await set(ref(database, "aboutusconfig"), config);
            alert("About Us configuration saved successfully!");
        } catch (error) {
            console.error("Error saving About Us config:", error);
            alert("Error saving configuration.");
        }
    };

    if (loading) return <div className="HomeConfigEditor-loading">Loading configuration...</div>;

    return (
        <div className="HomeConfigEditor">
            <h2 className="HomeConfigEditor-title">Edit About Us Configuration</h2>

            {/* General Settings */}
            <div className="HomeConfigEditor-section">
                <h3 className="HomeConfigEditor-section-title">General Settings</h3>
                <label className="HomeConfigEditor-label">
                    Body Background Color:
                    <input
                        type="color"
                        className="HomeConfigEditor-input"
                        value={config.general?.bodyBackgroundColor || "#ffffff"}
                        onChange={(e) =>
                            setConfig((prev) => ({
                                ...prev,
                                general: { ...prev.general, bodyBackgroundColor: e.target.value },
                            }))
                        }
                    />
                </label>
            </div>

            {/* SEO Metadata Section */}
            <div className="HomeConfigEditor-section">
                <h3 className="HomeConfigEditor-section-title">SEO Metadata</h3>
                <label className="HomeConfigEditor-label">
                    Meta Description:
                    <textarea
                        className="HomeConfigEditor-textarea"
                        value={config.seo?.metaDescription || ""}
                        onChange={(e) =>
                            setConfig((prev) => ({
                                ...prev,
                                seo: { ...prev.seo, metaDescription: e.target.value },
                            }))
                        }
                        placeholder="Enter meta description..."
                    />
                </label>
                <label className="HomeConfigEditor-label">
                    Meta Keywords:
                    <input
                        type="text"
                        className="HomeConfigEditor-input"
                        value={config.seo?.metaKeywords || ""}
                        onChange={(e) =>
                            setConfig((prev) => ({
                                ...prev,
                                seo: { ...prev.seo, metaKeywords: e.target.value },
                            }))
                        }
                        placeholder="Enter comma separated keywords..."
                    />
                </label>
                <button
                    className="HomeConfigEditor-button HomeConfigEditor-delete-button"
                    onClick={() => setConfig((prev) => ({ ...prev, seo: {} }))}
                >
                    Delete SEO Metadata
                </button>
            </div>

            {/* Hero Section */}
            <div className="HomeConfigEditor-section">
                <h3 className="HomeConfigEditor-section-title">Hero Section</h3>
                <div className="HomeConfigEditor-file-group">
                    <label className="HomeConfigEditor-label">Desktop Hero Image:</label>
                    {config.hero?.desktopImageUrl && (
                        <div className="HomeConfigEditor-preview">
                            <img
                                src={config.hero.desktopImageUrl}
                                alt="Desktop Hero"
                                className="HomeConfigEditor-preview-image"
                            />
                            <button
                                className="HomeConfigEditor-button HomeConfigEditor-delete-button"
                                onClick={() => {
                                    deleteFile(config.hero.desktopImageUrl);
                                    setConfig((prev) => ({
                                        ...prev,
                                        hero: { ...prev.hero, desktopImageUrl: "" },
                                    }));
                                }}
                            >
                                Delete
                            </button>
                        </div>
                    )}
                    <input
                        type="file"
                        accept="image/*"
                        className="HomeConfigEditor-input-file"
                        ref={heroDesktopInputRef}
                        onChange={handleHeroDesktopChange}
                    />
                </div>
                <div className="HomeConfigEditor-file-group">
                    <label className="HomeConfigEditor-label">Mobile Hero Image (PH View):</label>
                    {config.hero?.mobileImageUrl && (
                        <div className="HomeConfigEditor-preview">
                            <img
                                src={config.hero.mobileImageUrl}
                                alt="Mobile Hero"
                                className="HomeConfigEditor-preview-image"
                            />
                            <button
                                className="HomeConfigEditor-button HomeConfigEditor-delete-button"
                                onClick={() => {
                                    deleteFile(config.hero.mobileImageUrl);
                                    setConfig((prev) => ({
                                        ...prev,
                                        hero: { ...prev.hero, mobileImageUrl: "" },
                                    }));
                                }}
                            >
                                Delete
                            </button>
                        </div>
                    )}
                    <input
                        type="file"
                        accept="image/*"
                        className="HomeConfigEditor-input-file"
                        ref={heroMobileInputRef}
                        onChange={handleHeroMobileChange}
                    />
                </div>
                <div className="HomeConfigEditor-subsection">
                    <label className="HomeConfigEditor-label">
                        Hero Background Color:
                        <input
                            type="color"
                            className="HomeConfigEditor-input"
                            value={config.hero?.backgroundColor || "#ffffff"}
                            onChange={(e) => handleSimpleChange("hero", "backgroundColor", e.target.value)}
                        />
                    </label>
                    <label className="HomeConfigEditor-label">
                        Order:
                        <input
                            type="number"
                            className="HomeConfigEditor-input"
                            value={config.hero?.order || ""}
                            onChange={(e) => handleSimpleChange("hero", "order", e.target.value)}
                            min="1"
                        />
                    </label>
                    <label className="HomeConfigEditor-label">
                        Hero Text:
                        <textarea
                            className="HomeConfigEditor-textarea"
                            value={config.hero?.text || ""}
                            onChange={(e) => handleSimpleChange("hero", "text", e.target.value)}
                        />
                    </label>
                </div>
            </div>

            {/* Attraction Section */}
            <div className="HomeConfigEditor-section">
                <h3 className="HomeConfigEditor-section-title">Attraction Section</h3>
                <label className="HomeConfigEditor-label">
                    Section Order:
                    <input
                        type="number"
                        className="HomeConfigEditor-input"
                        value={config.attraction?.order || ""}
                        onChange={(e) =>
                            setConfig((prev) => ({
                                ...prev,
                                attraction: {
                                    ...prev.attraction,
                                    order: parseInt(e.target.value),
                                },
                            }))
                        }
                        min="1"
                    />
                </label>
                <div className="HomeConfigEditor-file-group">
                    <label className="HomeConfigEditor-label">
                        Add Attraction Images:
                        <input
                            type="file"
                            accept="image/*"
                            multiple
                            className="HomeConfigEditor-input-file"
                            ref={attractionInputRef}
                            onChange={handleAttractionMultipleChange}
                        />
                    </label>
                </div>
                <div className="HomeConfigEditor-attraction-list">
                    {config.attraction?.images &&
                        config.attraction.images.map((item, index) => (
                            <div key={item.id || index} className="HomeConfigEditor-array-item">
                                {item.imageUrl && (
                                    <div className="HomeConfigEditor-preview">
                                        <img
                                            src={item.imageUrl}
                                            alt={`Attraction ${index}`}
                                            className="HomeConfigEditor-preview-image"
                                        />
                                        <button
                                            className="HomeConfigEditor-button HomeConfigEditor-delete-button"
                                            onClick={() => removeAttractionImage(index)}
                                        >
                                            Remove Image
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                </div>
            </div>

            {/* Certificates Section */}
            <div className="HomeConfigEditor-section">
                <h3 className="HomeConfigEditor-section-title">Certificates Section</h3>
                {config.certificates &&
                    config.certificates.map((item, index) => (
                        <div key={item.id || index} className="HomeConfigEditor-array-item">
                            <div className="HomeConfigEditor-file-group">
                                <label className="HomeConfigEditor-label">Certificate Image:</label>
                                {item.imageUrl && (
                                    <div className="HomeConfigEditor-preview">
                                        <img
                                            src={item.imageUrl}
                                            alt={`Certificate ${index}`}
                                            className="HomeConfigEditor-preview-image"
                                        />
                                        <button
                                            className="HomeConfigEditor-button HomeConfigEditor-delete-button"
                                            onClick={() => {
                                                deleteFile(item.imageUrl);
                                                const updatedCertificates = [...config.certificates];
                                                updatedCertificates[index].imageUrl = "";
                                                setConfig((prev) => ({
                                                    ...prev,
                                                    certificates: updatedCertificates,
                                                }));
                                            }}
                                        >
                                            Remove Image
                                        </button>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="HomeConfigEditor-input-file"
                                    onChange={(e) => handleCertificateImageChange(index, e)}
                                />
                            </div>
                            <label className="HomeConfigEditor-label">
                                Description:
                                <textarea
                                    className="HomeConfigEditor-textarea"
                                    value={item.description || ""}
                                    onChange={(e) => {
                                        const updatedCertificates = [...config.certificates];
                                        updatedCertificates[index].description = e.target.value;
                                        setConfig((prev) => ({
                                            ...prev,
                                            certificates: updatedCertificates,
                                        }));
                                    }}
                                />
                            </label>
                            <label className="HomeConfigEditor-label">
                                Flex Direction (row or row-reverse):
                                <select
                                    className="HomeConfigEditor-input"
                                    value={item.flexDirection || "row"}
                                    onChange={(e) => {
                                        const updatedCertificates = [...config.certificates];
                                        updatedCertificates[index].flexDirection = e.target.value;
                                        setConfig((prev) => ({
                                            ...prev,
                                            certificates: updatedCertificates,
                                        }));
                                    }}
                                >
                                    <option value="row">Image Left, Text Right</option>
                                    <option value="row-reverse">Text Left, Image Right</option>
                                </select>
                            </label>
                            <label className="HomeConfigEditor-label">
                                Background Color:
                                <input
                                    type="color"
                                    className="HomeConfigEditor-input"
                                    value={item.backgroundColor || "#ffffff"}
                                    onChange={(e) => {
                                        const updatedCertificates = [...config.certificates];
                                        updatedCertificates[index].backgroundColor = e.target.value;
                                        setConfig((prev) => ({
                                            ...prev,
                                            certificates: updatedCertificates,
                                        }));
                                    }}
                                />
                            </label>
                            <label className="HomeConfigEditor-label">
                                Order:
                                <input
                                    type="number"
                                    className="HomeConfigEditor-input"
                                    value={item.order || ""}
                                    onChange={(e) => {
                                        const updatedCertificates = [...config.certificates];
                                        updatedCertificates[index].order = parseInt(e.target.value);
                                        setConfig((prev) => ({
                                            ...prev,
                                            certificates: updatedCertificates,
                                        }));
                                    }}
                                    min="1"
                                />
                            </label>
                            <button
                                className="HomeConfigEditor-button HomeConfigEditor-delete-button"
                                onClick={() => deleteArrayItem("certificates", index)}
                            >
                                Delete Certificate
                            </button>
                        </div>
                    ))}
                <button
                    className="HomeConfigEditor-button"
                    onClick={() => addArrayItem("certificates")}
                >
                    Add Certificate
                </button>
            </div>

            <button
                className="HomeConfigEditor-button HomeConfigEditor-save-button"
                onClick={handleSaveConfig}
            >
                Save Configuration
            </button>
        </div>
    );
};

export default AboutUsConfigEditor;
